import * as React from 'react';
import { createRoot } from "react-dom/client";

interface ReactComponentProps {
    component?: string;
    componentProps?: Record<string, any>;
    provider?: React.ComponentType<{ children: React.ReactNode }>;
}

// Cache for dynamically loaded components
const componentCache: Record<string, React.ComponentType<any>> = {};

/**
 * Entrypoint for rendering a React component from Blazor.
 * @param renderElement The DOM element to render into.
 * @param options Options including component path, props, and optional provider.
 * @returns Cleanup function to unmount.
 */
export default function App(renderElement: HTMLElement, options: ReactComponentProps = {}) {
    const {
        component: componentPath,
        componentProps = {},
        provider: Provider
    } = options;

    const root = createRoot(renderElement);

    // Function to dynamically load and render a component
    const loadAndRenderComponent = async () => {
        if (!componentPath) {
            root.render(<h1>No component path provided</h1>);
            return;
        }
        try {
            let Component = componentCache[componentPath];
            if (!Component) {
                const module = await import(/* @vite-ignore */ componentPath);
                Component = module.default || module;
                componentCache[componentPath] = Component;
            }
            const content = <Component {...componentProps} />;
            root.render(
                Provider ? <Provider>{content}</Provider> : content
            );
        } catch (error) {
            console.error(`Error loading component from path: ${componentPath}`, error);
            root.render(
                <div>
                    <h1>Error Loading Component</h1>
                    <p>Failed to load component from: {componentPath}</p>
                    <pre>{error instanceof Error ? error.message : String(error)}</pre>
                </div>
            );
        }
    };

    root.render(<div></div>);
    loadAndRenderComponent();

    return () => {
        root.unmount();
    };
}
